import { supabase } from '../supabaseClient';

export type ConnectionType = 'harvest' | 'sharepoint' | 'docusign' | 'openai';
export type ConnectionStatus = 'active' | 'inactive' | 'error' | 'pending_auth';

export interface Connection {
  id: string;
  userId: string;
  connectionType: ConnectionType;
  connectionName: string;
  config: Record<string, any>;
  status: ConnectionStatus;
  lastSyncAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

class ConnectionService {
  /**
   * Get all connections for current user
   */
  async getConnections(): Promise<Connection[]> {
    const { data, error } = await supabase
      .from('connections')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return (data || []).map(this.transformConnection);
  }
  
  /**
   * Get connection by type
   */
  async getConnectionByType(type: ConnectionType): Promise<Connection | null> {
    const { data, error } = await supabase
      .from('connections')
      .select('*')
      .eq('connection_type', type)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    
    return this.transformConnection(data);
  }
  
  /**
   * Delete connection
   */
  async deleteConnection(id: string): Promise<void> {
    const { error } = await supabase
      .from('connections')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
  
  /**
   * Update connection status
   */
  async updateConnectionStatus(
    id: string, 
    status: ConnectionStatus, 
    lastError?: string
  ): Promise<void> {
    const { error } = await supabase
      .from('connections')
      .update({ 
        status, 
        last_error: lastError || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);
    
    if (error) throw error;
  }
  
  /**
   * Save SharePoint connection (simple config storage)
   */
  async saveSharePointConnection(config: {
    tenantId: string;
    clientId: string;
    siteId: string;
    driveId: string;
  }): Promise<Connection> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data, error } = await supabase
      .from('connections')
      .upsert({
        user_id: user.id,
        connection_type: 'sharepoint',
        connection_name: 'Microsoft SharePoint',
        config,
        status: 'active',
      })
      .select()
      .single();
    
    if (error) throw error;
    
    return this.transformConnection(data);
  }
  
  private transformConnection(row: any): Connection {
    return {
      id: row.id,
      userId: row.user_id,
      connectionType: row.connection_type,
      connectionName: row.connection_name,
      config: row.config,
      status: row.status,
      lastSyncAt: row.last_sync_at,
      lastError: row.last_error,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}

export const connectionService = new ConnectionService();

